import { useMemo } from 'react'
import { useAuth } from './useAuth'
import { useUserEventInterests } from './useUserEventInterests'
import { useSocialEventData } from './useSocialEventData'
import { attendingEventIds, useEventsByIds } from './useEventsByIds'
import { useVisibleEvents } from './useVisibleEvents'
import { useFrozenValue } from './useFrozenValue'
import { useHomeRegion } from './useHomeRegion'
import { useEventRegions } from './useEventRegions'
import { useGuestGuard } from './useGuestGuard'
import { interestPrompt } from '../lib/guestPrompts'
import { isEventActive } from '../lib/formatDate'
import { byStartDateAsc } from '../lib/utils'
import { isNearby } from '@shared/regions'
import { useMinuteNow } from './useMinuteNow'

/**
 * Home's "my plans" data: the viewer's upcoming going/interested events in any
 * region, plus upcoming events their friends are into or
 * their groups linked that the viewer isn't part of, near the viewer's chosen
 * region (the same `isNearby` rule Discover uses: festivals reach drivable
 * regions, parties don't). The viewer's own plans aren't region-filtered —
 * they're plans, wherever they are.
 *
 * Past plans (`pastMyEvents`, `pastFriendEvents`, same rules) are kept apart
 * for the calendar, which pages back through earlier months.
 *
 * Which events are listed freezes once loaded, so un-RSVPing from a card
 * doesn't yank it out from under the tap; statuses stay live.
 */
export function usePlansFeed() {
  const { user, signedInUid, loading: authLoading } = useAuth()
  const { eventStatuses, loading: interestsLoading, setEventInterest } = useUserEventInterests(user?.uid ?? null)
  const social = useSocialEventData(eventStatuses)
  const { guard } = useGuestGuard()
  const guardedSetEventInterest = guard(setEventInterest, interestPrompt('event_interest'))
  const { currentUserId, facepileData, allGroupEvents } = social

  const myIds = useMemo(() => attendingEventIds(eventStatuses), [eventStatuses])
  // Facepile, not raw friend interests: group-only co-members count only on
  // events their group linked, the same rule the cards' facepiles follow.
  const crewEventIds = useMemo(() => {
    const ids = new Set<string>()
    facepileData.forEach((b, id) => {
      if (b.userIds.some(uid => uid !== currentUserId)) ids.add(id)
    })
    allGroupEvents.forEach(ge => ids.add(ge.eventId))
    return ids
  }, [facepileData, allGroupEvents, currentUserId])
  // `not_going` stays: people use it to clear an RSVP, not to rule the event out.
  const friendIds = useMemo(() => [...crewEventIds].filter(id => !myIds.includes(id)), [crewEventIds, myIds])

  const mine = useEventsByIds(myIds)
  const friends = useEventsByIds(friendIds)
  const visibleFriends = useVisibleEvents(friends.events, eventStatuses, allGroupEvents)
  const { regionId, isEverywhere } = useHomeRegion()
  const { regions, loading: regionsLoading } = useEventRegions(visibleFriends)

  const now = useMinuteNow()
  const live = useMemo(() => {
    const nearbyFriends = visibleFriends.filter(e => isEverywhere || isNearby(regionId, regions.get(e.id), e.type))
    return {
      mine: mine.events.filter(e => isEventActive(e, now)).sort(byStartDateAsc),
      friends: nearbyFriends.filter(e => isEventActive(e, now)).sort(byStartDateAsc),
      pastMine: mine.events.filter(e => !isEventActive(e, now)),
      pastFriends: nearbyFriends.filter(e => !isEventActive(e, now)),
    }
  }, [mine.events, visibleFriends, now, isEverywhere, regionId, regions])

  const ready =
    !authLoading && !interestsLoading && !mine.pending && !social.socialLoading && !friends.pending && !regionsLoading
  const resetKey = `${user?.uid ?? 'guest'}:${regionId}`
  const frozen = useFrozenValue(live, ready, resetKey)
  // An RSVP change re-keys the by-id queries, which briefly reads as loading;
  // once the lists have frozen that must not drop the page back to skeletons.
  const settled = useFrozenValue(ready, ready, resetKey)

  return {
    user,
    signedInUid,
    authLoading,
    interestsLoading,
    eventStatuses,
    guardedSetEventInterest,
    ...social,
    /** Live: events friends or co-members are on, or a group linked. */
    crewEventIds,
    myEvents: frozen.mine,
    friendEvents: frozen.friends,
    pastMyEvents: frozen.pastMine,
    pastFriendEvents: frozen.pastFriends,
    ready: settled,
    /** Nothing of the viewer's own to render yet. */
    loading: !settled && (authLoading || interestsLoading || (mine.loading && myIds.length > 0)),
    friendsLoading: friends.loading && friendIds.length > 0,
    error: mine.error ?? friends.error ?? social.socialError,
  }
}
